import NavigationSidebar from '../common/NavigationSidebar';
import { currentDashboard } from '../../utils/roleWorkspace';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getSidebarLogo } from '../../utils/logoUtils';
import { isFeedFeatureEnabled } from '../../utils/featureFlags';
import { logoutApi } from '../../services/auth/authService';
import { revokeCurrentWebPushToken } from '../../services/webPush/firebaseWebPush';
import { clearAllLocalLogoutData } from '../../utils/logoutCleanup';

function SideBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // State for mobile sidebar visibility
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  // Track if we're on mobile or not
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Check screen size on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-hide sidebar when resizing to desktop
      if (window.innerWidth >= 768) {
        setShowMobileSidebar(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile sidebar
  const toggleMobileSidebar = () => {
    setShowMobileSidebar(!showMobileSidebar);
  };

  const isActive = (path) => {
    if (path === '/user/courses' && (
      location.pathname.startsWith('/user/courses/courseDetails') ||
      location.pathname.startsWith('/user/courses/orderDetails') ||
      location.pathname.startsWith('/user/courses/Result') ||
      location.pathname.startsWith('/user/courses/WatchCourse') ||
      location.pathname.startsWith('/user/resultvideo')
    )) {
      return 'active';
    }
    if (path === '/user/AllClassroom' && (
      location.pathname.startsWith('/user/AllClassroom/classroomDetails') ||
      location.pathname.startsWith('/user/assignmentsQuestions')
    )) {
      return 'active';
    }
    return location.pathname === path ? 'active' : '';
  };

  const handleLinkClick = () => {
    // Close sidebar on mobile when clicking a link
    if (isMobile) {
      setShowMobileSidebar(false);
    }
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await revokeCurrentWebPushToken();
      // Optionally call logout API
      await logoutApi();

      // Clear all local/browser data for the signed-in user
      await clearAllLocalLogoutData();

      // Redirect to login page
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
      await clearAllLocalLogoutData();
      navigate('/');
    }
  };


  // Mobile toggle button to be rendered in the layout

    // Standard sidebar content
  const SidebarContent = () => {
    const showFeed = isFeedFeatureEnabled();

    return (
      <div className={"tw-bg-[#fff] tw-h-[calc(100vh_-_78px)] tw-overflow-y-auto tw-w-full [&::-webkit-scrollbar]:tw-w-[5px] [&::-webkit-scrollbar-track]:tw-bg-[transparent] [&::-webkit-scrollbar-thumb]:tw-bg-[rgba(0,_0,_0,_0.1)] [&::-webkit-scrollbar-thumb]:tw-rounded-[10px] [&::-webkit-scrollbar-thumb:hover]:tw-bg-[rgba(0,_0,_0,_0.2)] sidebar-nav tw-py-2"}>
        {currentDashboard() === '/admin/dashboard' && <Link to="/admin/dashboard" className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4"} onClick={handleLinkClick}>
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center"><Icon icon="fluent:settings-24-regular" width="22" height="22" /></div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Management</span>
        </Link>}
        <Link
          to="/user/dashboard"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/dashboard") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:grid-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Dashboard</span>
        </Link>

        {showFeed && (
          <Link
            to="/user/feed"
            className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/feed") ? "active" : ""}`}
            onClick={handleLinkClick}
          >
            <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
              <Icon icon="fluent:news-24-regular" width="22" height="22" />
            </div>
            <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Feed</span>
          </Link>
        )}

        <Link
          to="/user/profile"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/profile") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:person-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Profile</span>
        </Link>

        <Link
          to="/user/courses"
          className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/courses") ? "active" : ""}`}
          onClick={handleLinkClick}
        >
          <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
            <Icon icon="fluent:book-24-regular" width="22" height="22" />
          </div>
          <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Courses</span>
        </Link>

      <Link
        to="/user/AllClassroom"
        className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/AllClassroom") ? "active" : ""}`}
        onClick={handleLinkClick}
      >
        <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
          <Icon icon="fluent:desktop-24-regular" width="22" height="22" />
        </div>
        <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Classrooms</span>
      </Link>

      <Link
        to="/user/certificates"
        className={`tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item tw-flex tw-items-center tw-py-2 tw-px-4   ${isActive("/user/certificates") ? "active" : ""}`}
        onClick={handleLinkClick}
      >
        <div className="tw-w-[36px] tw-h-[36px] tw-rounded-[8px] tw-text-[inherit] nav-icon-wrapper tw-me-4 tw-flex tw-items-center tw-justify-center">
          <Icon icon="fluent:document-ribbon-24-regular" width="22" height="22" />
        </div>
        <span className="tw-text-[15px] tw-tracking-[0.2px] nav-text">Certificates</span>
      </Link>

      {/* Help Center Card */}
      <div
        className="tw-mx-auto tw-mt-6 tw-p-4 tw-rounded-xl tw-text-center"
        style={{
          backgroundColor: '#f0f2ff',
          width: '320px',
          maxWidth: '100%'
        }}
      >
        <div className="tw-mb-2">
          <Icon icon="fluent:question-circle-24-regular" width="28" height="28" color="#4361ee" />
        </div>
        <h6 className="tw-mb-1" style={{ fontSize: '14px' }}>Help center</h6>
        <p className="!tw-text-[12px] tw-text-sm tw-mb-2" style={{ color: '#6b7280', fontSize: '12px' }}>
          Any problem?<br />
          send us a message
        </p>
        <Link
          to="/user/help"
          className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] tw-rounded-[0.375rem] tw-pl-3 tw-pr-3 tw-pt-1.5 tw-pb-1.5 tw-text-[0.75rem] tw-leading-[1rem] ui-button ui-button-primary ui-button-sm tw-w-full"}
          style={{
            backgroundColor: '#4361ee',
            border: 'none',
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '13px'
          }}
        >
          Go to help center
        </Link>
      </div>
    </div>
  );
  };

  const logoutButton = (
    <button
      type="button"
      className={"tw-flex tw-w-full tw-items-center tw-gap-4 tw-border-0 tw-rounded-[.5625rem] tw-bg-[transparent] tw-p-[.6875rem_.875rem] tw-text-[#e11d48] tw-text-[.875rem] tw-font-semibold tw-text-left [transition:background-color_.15s,_color_.15s] [&:hover:not(:disabled)]:tw-bg-[#fff1f2] [&:hover:not(:disabled)]:tw-text-[#be123c] [&:focus-visible]:[outline:2px_solid_#e11d48] [&:focus-visible]:[outline-offset:2px] [&:disabled]:tw-cursor-wait [&:disabled]:tw-opacity-[.65] workspace-logout-button"}
      onClick={handleLogout}
      disabled={isLoggingOut}
      aria-busy={isLoggingOut}
    >
      <Icon icon="fluent:sign-out-24-regular" width="22" height="22" aria-hidden="true" />
      <span>{isLoggingOut ? 'Signing out…' : 'Logout'}</span>
    </button>
  );

  const sidebarLogo = getSidebarLogo();

  return <NavigationSidebar isMobile={isMobile} open={showMobileSidebar} onToggle={toggleMobileSidebar} onClose={() => setShowMobileSidebar(false)} logo={sidebarLogo} home="/user/dashboard" footer={logoutButton}><SidebarContent /></NavigationSidebar>;
}

export default SideBar;
